import { useQuery } from '@tanstack/react-query';
import { useState, type CSSProperties, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import simbolo from '@libreta/ui/marca/simbolo.svg';
import { pedir } from '../api/cliente';
import { useTema, type Tema } from '../comun/tema';
import { useSesion, useYo } from '../sesion/sesion';
import type { CuotaACobrar, PaginaConResumen, ResumenCuotas } from '@libreta/contratos';
import { Boton } from './Controles';
import { Icono } from './Icono';
import { Avatar } from './Piezas';

const SIGUIENTE_TEMA: Record<Tema, Tema> = { claro: 'oscuro', oscuro: 'claro' };
const NOMBRE_TEMA: Record<Tema, string> = { claro: 'Pasar a tema oscuro', oscuro: 'Pasar a tema claro' };

export function Marco({ children }: { children: ReactNode }) {
  const yo = useYo();
  const sesion = useSesion();
  const [tema, setTema] = useTema();
  const [selector, setSelector] = useState(false);
  const [abierta, setAbierta] = useState(false);
  const ubicacion = useLocation();

  // La cuenta de cuotas vencidas junto al menú de Cobranzas: lo urgente se ve desde cualquier pantalla.
  const puedeCobrar = yo.permisos.includes('pagos.ver') || yo.permisos.includes('pagos.registrar');
  const vencidas = useQuery({
    queryKey: ['cobranzas', 'VENCIDAS', 'cuenta'],
    queryFn: () => pedir<PaginaConResumen<CuotaACobrar, ResumenCuotas>>('/cobranzas/cuotas?alcance=VENCIDAS&porPagina=1').then((p) => p.total),
    enabled: Boolean(yo.entidad) && puedeCobrar,
    staleTime: 60_000,
  });

  const otrasEntidades = yo.entidades.filter((e) => e.id !== yo.entidad?.id);
  const puedeCambiar = otrasEntidades.length > 0 || yo.usuario.esPlataforma;

  return (
    <>
      {yo.soporte ? (
        <div className="franja-soporte" role="status">
          <Icono nombre="escudo" />
          Estás dentro de {yo.entidad?.nombre} como soporte de RASP. Todo lo que hagas queda registrado a tu nombre.
          <Boton tamano="chico" onClick={() => void sesion.volverAPlataforma()}>Volver a la plataforma</Boton>
        </div>
      ) : null}
      <Boton variante="secundario" icono="menu" className="boton-menu-movil" onClick={() => setAbierta(!abierta)} aria-label="Abrir menú" />
      <div className="marco">
        <aside className={`barra ${abierta ? 'barra--abierta' : ''}`}>
          <div className="barra__marca">
            <img className="barra__logo" src={simbolo} alt="" />
            <div className="barra__nombre">
              <strong>Libreta</strong>
              <span>{yo.entidad ? 'Ventas en cuotas' : 'Administración'}</span>
            </div>
          </div>

          {yo.entidad || yo.usuario.esPlataforma ? (
            <button
              type="button"
              className="barra__entidad"
              onClick={() => puedeCambiar && setSelector(!selector)}
              aria-expanded={selector}
              disabled={!puedeCambiar}
            >
              <Icono nombre={yo.entidad ? 'caja' : 'plataforma'} />
              <span>
                {yo.entidad?.nombre ?? 'Plataforma'}
                <small>{yo.rol}</small>
              </span>
              {puedeCambiar ? <Icono nombre="cambiar" /> : null}
            </button>
          ) : null}

          {selector ? (
            <div className="selector-entidades">
              {otrasEntidades.map((entidad) => (
                <button key={entidad.id} type="button" onClick={() => { setSelector(false); void sesion.cambiarEntidad(entidad.id); }}>
                  <strong>{entidad.nombre}</strong>
                  <small>{entidad.rol}</small>
                </button>
              ))}
              {yo.usuario.esPlataforma && yo.entidad ? (
                <button type="button" onClick={() => { setSelector(false); void sesion.volverAPlataforma(); }}>
                  <strong>Administración de Libreta</strong>
                  <small>Plataforma RASP</small>
                </button>
              ) : null}
            </div>
          ) : null}

          <nav className="menu" aria-label="Principal">
            {yo.entidad
              ? yo.menu.map((entrada, i) => (
                  <NavLink
                    key={entrada.clave}
                    to={entrada.bloqueado ? `/no-incluido/${entrada.clave.toLowerCase()}` : entrada.ruta}
                    end={entrada.ruta === '/'}
                    onClick={() => setAbierta(false)}
                    className={({ isActive }) => `menu__item ${isActive ? 'menu__item--activo' : ''} ${entrada.bloqueado ? 'menu__item--bloqueado' : ''}`}
                    style={{ '--i': i } as CSSProperties}
                  >
                    <Icono nombre={entrada.icono} />
                    {entrada.nombre}
                    {entrada.bloqueado ? <Icono nombre="candado" clase="menu__candado" titulo="No incluido en el plan" /> : null}
                    {entrada.clave === 'COBRANZAS' && vencidas.data ? (
                      <span className="menu__cuenta" title="Cuotas vencidas">{vencidas.data}</span>
                    ) : null}
                  </NavLink>
                ))
              : (
                  <NavLink to="/" end className={({ isActive }) => `menu__item ${isActive ? 'menu__item--activo' : ''}`} style={{ '--i': 0 } as CSSProperties}>
                    <Icono nombre="plataforma" />
                    Entidades y planes
                  </NavLink>
                )}
          </nav>

          <div className="barra__pie">
            <div className="barra__persona">
              <Avatar nombre={yo.usuario.nombre} />
              <div>
                <strong>{yo.usuario.nombre}</strong>
                <small>{yo.usuario.email}</small>
              </div>
            </div>
            <div className="barra__herramientas">
              <NavLink to="/mi-cuenta" onClick={() => setAbierta(false)} className={({ isActive }) => `boton boton--fantasma boton--chico ${isActive ? 'boton--activo' : ''}`}>
                <Icono nombre="usuario" />
                <span>Mi cuenta</span>
              </NavLink>
              <Boton variante="fantasma" tamano="chico" icono={tema === 'claro' ? 'luna' : 'sol'} onClick={() => setTema(SIGUIENTE_TEMA[tema])} title={NOMBRE_TEMA[tema]} aria-label={NOMBRE_TEMA[tema]} />
            </div>
            {/* Salir tiene que verse sin buscarlo: botón entero con texto, no un ícono. */}
            <Boton variante="secundario" icono="salir" className="barra__salir" onClick={() => void sesion.salir()}>
              Cerrar sesión
            </Boton>
          </div>
        </aside>
        <main className="contenido" key={ubicacion.pathname.split('/')[1]}>
          <div className="contenido__ancho">{children}</div>
        </main>
      </div>
    </>
  );
}
